<template>
  <div v-if="canEnforceAuthorityService" v-bind="$attrs">
    <CustomSwitchWrapper
      data-test-id="enforce-authority"
      :initial-value="enforceAuthorityService"
      :label="i18n.t('labels.enforce_authority_service').toString()"
      label-class="drawer-subhead"
      :switch-id="i18n.t('labels.enforce_authority_service').toString()"
      @value-changed="onEnforceChanged"
    />

    <p class="enforce-authority-description">
      {{ i18n.t('texts.enforce_authority_service_description') }}
    </p>

    <v-row
      v-if="enforceAuthorityService"
      class="d-flex align-center authority-service deep-footnote my-3"
      no-gutters
    >
      <div class="mt-1 drawer-footnote ml-2">
        {{ i18n.t('labels.id_service') }}
      </div>
      <AuthorityServiceMenu
        v-if="availableAuthorityServices.length > 0"
        :authority-service="authorityServiceObject"
        :authority-services="availableAuthorityServices"
        :signature-mode="signatureMode"
      />
      <v-spacer />
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CustomSwitchWrapper from '@/components/inputs/CustomSwitchWrapper.vue';
import AuthorityServiceMenu from '@/components/menus/AuthorityServiceMenu.vue';
import { useSigningWorkflow } from '@/composables/create/useSigningWorkflow';
import { useEmitter } from '@/composables/useEmitter';
import type { AuthorityService } from '@/types/enums/AuthorityService';

defineOptions({
  inheritAttrs: false
});

const i18n = useI18n();
const {
  signatureMode,
  enforceAuthorityService,
  authorityService,
  canEnforceAuthorityService,
  availableAuthorityServices,
  setEnforceAuthorityService,
  setAuthorityService,
  refetchAvailableModes
} = useSigningWorkflow();
const emitter = useEmitter();

const authorityServiceObject = computed(() => ({
  key: authorityService.value ?? undefined
}));

function onEnforceChanged(value: boolean) {
  setEnforceAuthorityService(value);
}

function setAuthorityServiceFromMenu(serviceKey: AuthorityService) {
  const match = availableAuthorityServices.value.find(
    (service) => service.key === serviceKey
  );
  if (match) {
    setAuthorityService(match.key);
  }
}

watch(enforceAuthorityService, async (value) => {
  if (value) {
    await refetchAvailableModes();
    if (!authorityService.value && availableAuthorityServices.value[0]?.key) {
      setAuthorityService(availableAuthorityServices.value[0].key);
    }
  } else {
    setAuthorityService(null);
  }
});

onMounted(() => {
  emitter.$on('set-authority-service', setAuthorityServiceFromMenu);
});

onUnmounted(() => {
  emitter.$off('set-authority-service', setAuthorityServiceFromMenu);
});
</script>

<style scoped lang="scss">
.enforce-authority-description {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgb(var(--v-theme-grey-darken-1));
}
</style>
